import { Link } from "react-router-dom";
import SEO from "../components/common/SEO";
import PixelIcon from "../components/common/PixelIcon";

export default function WiseSight() {
  const features = [
    {
      title: "Evidence-Based Approach",
      description: "Uses orthographic mapping to help children truly understand words, not just memorize them",
      icon: "brain",
    },
    {
      title: "Spaced Repetition",
      description: "Smart algorithm ensures words are reviewed at optimal intervals for lasting memory",
      icon: "clock",
    },
    {
      title: "5-Step Teaching Method",
      description: "Proven routine: Hear & Say, Map Sounds, Read in Context, Quick Write, and Parent Grading",
      icon: "list",
    },
    {
      title: "No Pressure",
      description: "Gentle, encouraging design with no timers or anxiety-inducing elements",
      icon: "heart",
    },
  ];

  const privacyFeatures = [
    "100% Private - All data stays on your device",
    "No Ads - Ever",
    "No In-App Purchases",
    "No User Tracking",
    "COPPA Compliant",
    "Safe for Kids",
  ];

  const scienceFeatures = [
    "Orthographic mapping for word recognition",
    "Spaced repetition for long-term retention",
    "Multi-sensory learning (visual, auditory, kinesthetic)",
    "Explicit phoneme-grapheme connections",
  ];

  return (
    <>
      <SEO
        title="Wise Sight - Evidence-Based Sight Word Learning"
        description="Help your child master sight words using proven science of reading techniques. Ages 5-8."
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">Wise Sight</span>
              <span className="block text-blue-600 mt-2">Evidence-Based Sight Word Learning</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-xl text-gray-500">
              Help your child master sight words using proven science of reading techniques.
              Perfect for ages 5-8.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#features"
                className="rounded-md bg-blue-600 px-8 py-3 text-base font-medium text-white hover:bg-blue-700 transition-colors"
              >
                Learn More
              </a>
              <a
                href="#download"
                className="rounded-md bg-gray-100 px-8 py-3 text-base font-medium text-gray-900 hover:bg-gray-200 transition-colors"
              >
                Get the App
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* What Makes It Different Section */}
      <div id="features" className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              What Makes Wise Sight Different
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Built on science of reading research and designed for real learning
            </p>
          </div>

          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="flex justify-center">
                  <PixelIcon type={feature.icon} size={48} />
                </div>
                <h3 className="mt-6 text-lg font-semibold text-gray-900">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="bg-gray-50 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Key Features
              </h2>
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PixelIcon type="book" size={24} />
                  </div>
                  <p className="ml-3 text-base text-gray-700">
                    100+ High-Frequency Sight Words
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PixelIcon type="brain" size={24} />
                  </div>
                  <p className="ml-3 text-base text-gray-700">
                    Orthographic Mapping Activities
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PixelIcon type="document" size={24} />
                  </div>
                  <p className="ml-3 text-base text-gray-700">
                    Decodable Sentences
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PixelIcon type="chart" size={24} />
                  </div>
                  <p className="ml-3 text-base text-gray-700">
                    Progress Tracking
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PixelIcon type="clock" size={24} />
                  </div>
                  <p className="ml-3 text-base text-gray-700">
                    Daily Practice Sessions (5-8 minutes)
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PixelIcon type="lightbulb" size={24} />
                  </div>
                  <p className="ml-3 text-base text-gray-700">
                    Parent Tips & Coaching
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PixelIcon type="globe" size={24} />
                  </div>
                  <p className="ml-3 text-base text-gray-700">
                    Works Offline - No Internet Required
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 lg:mt-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Privacy & Safety
              </h3>
              <div className="bg-white rounded-lg shadow-sm p-6 space-y-3">
                {privacyFeatures.map((feature) => (
                  <div key={feature} className="flex items-center">
                    <PixelIcon type="check" size={20} />
                    <span className="ml-3 text-base text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Science of Reading Section */}
      <div className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Based on Science of Reading
            </h2>
            <p className="mt-4 text-lg text-gray-500 max-w-3xl mx-auto">
              Wise Sight implements research-backed methods proven to help children develop
              strong reading skills
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto">
            {scienceFeatures.map((feature) => (
              <div
                key={feature}
                className="bg-blue-50 rounded-lg p-6 text-center"
              >
                <p className="text-sm text-gray-700">{feature}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-xl text-gray-600 italic">
              Made with ❤️ by educators who care about effective reading instruction
            </p>
          </div>
        </div>
      </div>

      {/* Download Section */}
      <div id="download" className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Get Wise Sight
          </h2>
          <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto">
            Available on iOS and Android. Download today and start your child's journey to
            reading success!
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-6 justify-center">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <PixelIcon type="mobile" size={48} className="mx-auto" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">iOS App Store</h3>
              <p className="mt-2 text-sm text-gray-500">Coming Soon</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <PixelIcon type="mobile" size={48} className="mx-auto" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">Google Play Store</h3>
              <p className="mt-2 text-sm text-gray-500">Coming Soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="bg-gray-50 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Need Help?</h2>
            <div className="mt-6 flex flex-wrap gap-6 justify-center text-sm">
              <Link
                to="/wise-sight/support"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Support & FAQs
              </Link>
              <Link
                to="/wise-sight/privacy"
                className="text-gray-600 hover:text-gray-900"
              >
                Privacy Policy
              </Link>
              <Link
                to="/wise-sight/terms"
                className="text-gray-600 hover:text-gray-900"
              >
                Terms of Service
              </Link>
              <a
                href="mailto:support@polityinterfaces.com"
                className="text-gray-600 hover:text-gray-900"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-blue-600 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">
            Start Building Reading Success Today
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            Join families using evidence-based methods to help their children thrive
          </p>
          <div className="mt-8">
            <a
              href="#download"
              className="inline-block rounded-md bg-white px-8 py-3 text-base font-medium text-blue-600 hover:bg-blue-50 transition-colors"
            >
              Download Wise Sight
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
